import { useEffect, useMemo, useRef, useState } from "react";
import { trackAmplitudeEvent } from "../../amplitude";
import {
  EXTRA_NEW_WORDS_STEP,
  addExtraNewWords,
  buildPlayUrl,
  buildDefaultConfig,
  buildQueue,
  classifyUserType,
  freeCardsRemainingToday,
  isComplete as isQueueComplete,
  isDailyLimitReached,
  migrateLegacyPlan,
  newWordsLeftBeyondPlan,
  nextInQueue,
  resolveConfig,
  reviewAheadItem,
  todayString,
  turnOnNewWordsToFit,
  untappedReviews
} from "./todaysPlanService";
import { fetchTodaysPlanSettings, localKey, readLegacyPlan, readLocal, readTodaysExtraNewWords, saveTodaysPlanSettings, writeLocal } from "./todaysPlanStorage";

const sum = (items, valueOf) => items.reduce((s, item) => s + valueOf(item), 0);

export default function useTodaysPlan({
  collections,
  isPro,
  isSpeakingAvailable,
  isSystemTtsAvailable,
  isTranslateReviewsEnabled,
  languagePairingId,
  playOptions,
  todaysPlanSettingsUrl,
  username
}) {
  const extraNewWordsKey = localKey("ExtraNewWords", username, languagePairingId);
  const bannerDismissedKey = localKey("BannerDismissed", username, languagePairingId);
  const [savedConfig, setSavedConfig] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [extraNewWords, setExtraNewWords] = useState(() => readTodaysExtraNewWords(username, languagePairingId));
  const [bannerDismissedDate, setBannerDismissedDate] = useState(() => readLocal(bannerDismissedKey));
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const [isDailyLimitVisible, setIsDailyLimitVisible] = useState(false);
  const [saveFailure, setSaveFailure] = useState(null);
  const isConfigEditedRef = useRef(false);
  const hasTrackedViewedRef = useRef(false);
  const hasPlayedRef = useRef(false);
  const queueOptions = { isSpeakingAvailable, isSystemTtsAvailable };

  // The plan kept in this browser, once the server has it, so every page plays the same plan.
  const savedLegacyPlan = async () => {
    try {
      const migratedConfig = migrateLegacyPlan(buildDefaultConfig(collections, playOptions), collections, readLegacyPlan(username));
      if(!migratedConfig || isConfigEditedRef.current) return null;
      await saveTodaysPlanSettings(todaysPlanSettingsUrl, migratedConfig);
      return migratedConfig;
    } catch(e) {
      return null;
    }
  };

  useEffect(() => {
    let isCancelled = false;
    setIsLoadFailed(false);
    fetchTodaysPlanSettings(todaysPlanSettingsUrl)
      .then(async (config) => {
        const loadedConfig = config || await savedLegacyPlan();
        if(isCancelled) return;
        if(!isConfigEditedRef.current) setSavedConfig(loadedConfig);
        setIsLoaded(true);
      })
      .catch(() => {
        if(!isCancelled) setIsLoadFailed(true);
      });
    return () => { isCancelled = true; };
  }, [todaysPlanSettingsUrl, loadAttempt]);

  const config = useMemo(() => resolveConfig(collections, playOptions, savedConfig), [collections, playOptions, savedConfig]);

  const planConfig = useMemo(
    () => addExtraNewWords(config, collections, extraNewWords.extra),
    [config, collections, extraNewWords.extra]
  );

  const queue = useMemo(
    () => buildQueue(collections, planConfig, { ...queueOptions, isPro }),
    [collections, planConfig, isPro, isSpeakingAvailable, isSystemTtsAvailable]
  );

  const totalItems = sum(queue, (item) => item.total);
  const completedItems = sum(queue, (item) => item.completed);
  const isComplete = isQueueComplete(queue);
  const percent = totalItems > 0 ? Math.min(100, Math.round((completedItems / totalItems) * 100)) : 0;

  useEffect(() => {
    if(!isLoaded || hasTrackedViewedRef.current || !queue.length) return;
    hasTrackedViewedRef.current = true;

    const today = todayString();
    const firstViewedKey = localKey("FirstViewed", username);
    const firstViewed = readLocal(firstViewedKey);
    if(!firstViewed) writeLocal(firstViewedKey, today);

    trackAmplitudeEvent("todays_plan_viewed", {
      is_complete: isComplete,
      is_default_view: true,
      state: isComplete ? "complete" : completedItems > 0 ? "in_progress" : "zero",
      total_collections_queued: queue.length,
      total_new_words_queued: sum(queue.filter((i) => i.type === "new_words"), (i) => i.total),
      total_reviews_queued: sum(queue.filter((i) => i.type === "review"), (i) => i.total),
      user_type: classifyUserType(firstViewed, today)
    });
  }, [isLoaded, queue]);

  const changeConfig = (newConfig) => {
    const previousConfig = savedConfig;
    isConfigEditedRef.current = true;
    setSavedConfig(newConfig);
    saveTodaysPlanSettings(todaysPlanSettingsUrl, newConfig).catch(() => {
      setSavedConfig((current) => (current === newConfig ? previousConfig : current));
      setSaveFailure({ id: Date.now() });
    });
  };

  const nextItem = nextInQueue(queue) || (totalItems === 0 ? reviewAheadItem(collections, planConfig, queueOptions) : null);
  const nextCollection = nextItem && collections.find((c) => String(c.id) === nextItem.collectionId);
  const playUrl = nextCollection ? buildPlayUrl(nextCollection, nextItem, { isTranslateReviewsEnabled }) : null;

  const startPlaying = (event) => {
    if(isDailyLimitReached(collections, isPro)) {
      event.preventDefault();
      setIsDailyLimitVisible(true);
      return;
    }
    trackAmplitudeEvent("todays_plan_started", {
      first_collection_name: nextItem.collectionName,
      first_collection_type: nextItem.type,
      is_first_play_today: !hasPlayedRef.current
    });
    hasPlayedRef.current = true;
  };

  const togglePanel = () => {
    const isExpanding = !isPanelExpanded;
    setIsPanelExpanded(isExpanding);
    if(isExpanding) {
      trackAmplitudeEvent("todays_plan_summary_opened", {
        review_limits_active: collections.some((c) => c.maxReviewsPerDay !== null && c.maxReviewsPerDay !== undefined),
        total_available_reviews: sum(collections, (c) => c.numReadyForReview)
      });
    }
  };

  const openInfo = () => {
    trackAmplitudeEvent("todays_plan_info_opened");
    setIsInfoVisible(true);
  };

  const dismissBanner = () => {
    const today = todayString();
    writeLocal(bannerDismissedKey, today);
    setBannerDismissedDate(today);
    trackAmplitudeEvent("todays_plan_banner_dismissed");
  };

  const addMoreNewWords = () => {
    // Sized so exactly 30 remain even if today's new words overshot the planned total.
    const newWordsDoneToday = (cfg) => {
      const ids = cfg.collections.filter((cc) => cc.newWordsEnabled ?? cc.enabled).map((cc) => cc.collectionId);
      return sum(collections.filter((c) => ids.includes(String(c.id))), (c) => c.numNewToday || 0);
    };
    const remainingInPlan = sum(queue.filter((i) => i.type === "new_words"), (i) => i.total - i.completed);
    const fittedConfig = turnOnNewWordsToFit(config, collections, remainingInPlan + EXTRA_NEW_WORDS_STEP);
    if(fittedConfig !== config) changeConfig(fittedConfig);
    const doneToday = newWordsDoneToday(fittedConfig);
    const planned = sum(fittedConfig.collections.filter((cc) => cc.newWordsEnabled ?? cc.enabled), (cc) => cc.newWordsPerDay);
    const added = {
      count: extraNewWords.count + EXTRA_NEW_WORDS_STEP,
      date: todayString(),
      extra: Math.max(0, doneToday + remainingInPlan + EXTRA_NEW_WORDS_STEP - planned)
    };
    writeLocal(extraNewWordsKey, added);
    setExtraNewWords(added);
    trackAmplitudeEvent("todays_plan_more_words_added", {
      total_words_added_today: added.count,
      words_added: EXTRA_NEW_WORDS_STEP
    });
  };

  return {
    addMoreNewWords,
    changeConfig,
    closeDailyLimit: () => setIsDailyLimitVisible(false),
    closeInfo: () => setIsInfoVisible(false),
    completedItems,
    config,
    dismissBanner,
    freeCardsRemaining: isPro ? 0 : freeCardsRemainingToday(collections),
    hasMoreNewWords: newWordsLeftBeyondPlan(collections, queue) >= EXTRA_NEW_WORDS_STEP,
    isComplete,
    isDailyLimitVisible,
    isInfoVisible,
    isLoaded,
    isLoadFailed,
    isPanelExpanded,
    openInfo,
    percent,
    playUrl,
    queue,
    retryLoad: () => setLoadAttempt((attempt) => attempt + 1),
    saveFailure,
    showCompleteBanner: isComplete && bannerDismissedDate !== todayString(),
    startPlaying,
    togglePanel,
    totalItems,
    untappedReviews: isPro ? 0 : untappedReviews(collections, queue)
  };
}
